// D30 (roles prod promotion, Stage 3, 2026-09-25): solution search and tags on the two
// solution pages, as pure helpers (LEAF module, no imports) shared by the main solutions page
// and Solution Management and unit-tested.
//
// The two pages receive a solution's tags in different shapes: the main page
// (metaData.services) has tag IDS and reads the names separately - a read that answers 400 for
// a tenant with no tags - while Solution Management (metaData.servicesNew) has the tag objects
// ({ _id, name }). Everything below works on the one normalized shape [{ id, name }].

// A card shows at most this many tag chips; the rest are counted as "+N more".
export const MAX_TAG_CHIPS = 10;

// id -> name from the tag read's payload. Anything but a list (the 400 of a tenant with no
// tags, a read still loading or failed) is an empty map.
export function tagNameMap(tagList) {
  const names = {};
  (Array.isArray(tagList) ? tagList : []).forEach((t) => {
    if (t && t._id != null && t.name) names[String(t._id)] = t.name;
  });
  return names;
}

// A solution's tags as [{ id, name }], from either shape: tag ids (named through `names`) or
// tag objects. A tag without a known name is left out (it can be neither shown nor searched);
// a missing or empty list is [].
export function solutionTags(tags, names = {}) {
  const out = [];
  const seen = new Set();
  (Array.isArray(tags) ? tags : []).forEach((t) => {
    const isObj = !!t && typeof t === "object";
    const rawId = isObj ? (t._id != null ? t._id : t.id) : t;
    if (rawId == null || rawId === "") return;
    const id = String(rawId);
    const name = (isObj && t.name) || names[id];
    if (!name || seen.has(id)) return;
    seen.add(id);
    out.push({ id, name });
  });
  return out;
}

// The tags carried by at least one of `solutions`, each with how many solutions carry it,
// sorted by name - the only tags the shared filter lists.
export function tagsInUse(solutions, tagsOf) {
  const byId = {};
  (solutions || []).forEach((s) => {
    tagsOf(s).forEach((t) => {
      if (!byId[t.id]) byId[t.id] = { id: t.id, name: t.name, count: 0 };
      byId[t.id].count += 1;
    });
  });
  return Object.values(byId).sort((a, b) => a.name.localeCompare(b.name));
}

// Tag filter: no selected tag lets everything through; "any" (the default) needs one of the
// selected tags, "all" needs every one of them.
export function matchesTags(tags, selectedIds, mode) {
  if (!selectedIds || !selectedIds.length) return true;
  const held = new Set((tags || []).map((t) => t.id));
  return mode === "all"
    ? selectedIds.every((id) => held.has(String(id)))
    : selectedIds.some((id) => held.has(String(id)));
}

// Search: the solution's name, description or one of its tag names contains the text
// (case-insensitive, surrounding spaces ignored).
export function matchesSearch(solution, tags, query) {
  const q = String(query || "").trim().toLowerCase();
  if (!q) return true;
  return [
    solution && solution.name,
    solution && solution.description,
    ...(tags || []).map((t) => t.name),
  ].some((v) => String(v || "").toLowerCase().includes(q));
}

// The solutions a page lists: one filter over the page's own list, so the order is kept and a
// solution appears at most once however many selected tags it carries (the old main page
// concatenated one match list per selected tag, which showed a solution once per tag).
// Selected tags that no solution of the page carries any more are ignored, so a stale
// selection can never empty the page silently.
export function filterSolutions(solutions, { query, selectedIds, mode, tagsOf }) {
  const list = solutions || [];
  const inUse = new Set(tagsInUse(list, tagsOf).map((t) => t.id));
  const active = (selectedIds || []).map(String).filter((id) => inUse.has(id));
  return list.filter((s) => {
    const tags = tagsOf(s);
    return matchesTags(tags, active, mode) && matchesSearch(s, tags, query);
  });
}

// The chips a card shows (at most MAX_TAG_CHIPS) and the ones summed up as "+N more".
export function splitTagChips(tags, max = MAX_TAG_CHIPS) {
  const all = tags || [];
  return { shown: all.slice(0, max), hidden: all.slice(max) };
}
